/* eslint-disable jsx-a11y/label-has-associated-control */
/* eslint-disable jsx-a11y/label-has-for */
import React, { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import Modal from "@visiblethread/react-components/dist/components/Modal";
import clsx from "clsx";
import PropTypes from "prop-types";
import ColourPicker from "@visiblethread/react-components/dist/components/ColourPicker";
import {
  Tooltip,
  tooltipPlacement,
} from "@visiblethread/react-components/dist/components/Tooltip";
import Button, {
  buttonVariants,
} from "@visiblethread/react-components/dist/components/Button";
import Checkbox from "@visiblethread/react-components/dist/components/Checkbox";

import {
  displayWildcardPopup,
  editTerm,
  validateTerms,
} from "slices/dictionary/dictionarySlice";
import { checkedTypes } from "constants/types";
import {
  dictionaryDefaultColour,
  dictionaryTermTypes,
} from "constants/dictionary";
import {
  ERROR_MESSAGES,
  useValidatePopupExpression,
} from "hooks/dictionary/useValidatePopupExpression";
import {
  hasSpecialWildcardCharacters,
  isWildcardExpression,
} from "utils/dictionary";

import {
  generatePreview,
  getRegularExpressionGuidanceText,
  getWildcardExpressionGuidanceText,
  isExpressionValid,
  renderPreview,
} from "layout/dictionary/edit/helpers";
import { termProps } from "layout/dictionary/edit/types";
import WildcardSwitch from "layout/dictionary/edit/components/WildcardSwitch";
import SlidingPanel, {
  ANIMATION_DURATION,
  runHideAnimation,
} from "components/SlidingPanel";

const FOCUS_DELAY = 200;

const WildcardPopup = ({
  term,
  term: {
    name,
    description: defaultDescription,
    caseSensitive: defaultCaseSensitive,
    id,
    categoryId,
    dictionaryTermType: termType,
    colour: defaultColour,
  },
}) => {
  const [expression, setExpression] = useState(name || "");
  const [caseSensitive, setCaseSensitive] = useState(defaultCaseSensitive);
  const [colour, setColour] = useState(
    defaultColour || dictionaryDefaultColour
  );
  const [description, setDescription] = useState(defaultDescription || "");
  const [expert, setExpert] = useState(
    termType === dictionaryTermTypes.REGULAR_EXPRESSION
  );
  const [preview, setPreview] = useState([]);
  const [prevPreview, setPrevPreview] = useState([]);
  const [previewLengths, setPreviewLengths] = useState([]);
  const [errorConfirm, setErrorConfirm] = useState(false);
  const errorMessage = useValidatePopupExpression(
    expert,
    expression,
    caseSensitive,
    colour,
    description,
    term
  );
  const dispatch = useDispatch();
  const wrapperRef = useRef(null);
  const exprRef = useRef(null);
  const popupRef = useRef();

  const closePopup = () => {
    runHideAnimation(popupRef);

    // Wait for animation to finish and then close popup
    setTimeout(() => {
      dispatch(displayWildcardPopup({ displayed: false, term: null }));
    }, ANIMATION_DURATION);
  };

  const updatePreview = (value) => {
    setPrevPreview(preview);
    setPreview(generatePreview(previewLengths, value, 3));
    setPreviewLengths(previewLengths);
  };

  const handleExpressionChange = ({ target: { value } }) => {
    setExpression(value);

    if (hasSpecialWildcardCharacters(value) && isWildcardExpression(value)) {
      updatePreview(value);
    }
  };

  const handleSwitchChange = () => {
    setExpert(!expert);
  };

  const handleCaseSensitiveChange = () => {
    setCaseSensitive(!caseSensitive);
  };

  const handleColourChange = (newColour) => {
    setColour(newColour);
  };

  const handleDescriptionChange = ({ target: { value } }) => {
    setDescription(value);
  };

  const handleConfirmClick = () => {
    // If error is present display error layout for expression input and focus it
    if (errorMessage) {
      setErrorConfirm(true);
      exprRef.current.focus();
      return;
    }

    dispatch(
      editTerm({
        termId: id,
        categoryId,
        editedTerm: {
          ...term,
          name: expression,
          description,
          caseSensitive,
          dictionaryTermType: expert
            ? dictionaryTermTypes.REGULAR_EXPRESSION
            : dictionaryTermTypes.WILDCARD_EXPRESSION,
          colour,
        },
      })
    );

    closePopup();
    dispatch(validateTerms());
  };

  /* Once popup opened, focus expression input, and generate preview for expression */
  useEffect(() => {
    setTimeout(() => {
      exprRef.current?.focus();
    }, FOCUS_DELAY);

    if (
      hasSpecialWildcardCharacters(expression) &&
      isWildcardExpression(expression)
    ) {
      updatePreview(expression);
    }
  }, []);

  /* Update description based on preview */
  useEffect(() => {
    // Expression must be wildcard and shouldn't have errors
    if (expert || errorMessage === ERROR_MESSAGES.WILDCARD_NOT_VALID) return;

    const prePreviewString = prevPreview.join(", ");
    const previewString = preview.join(", ");

    if (description === "" && previewString !== "") {
      setDescription(`Matches: ${previewString}`);
    }

    const regex = new RegExp(
      `${prePreviewString.replace(/[-/\\^$*+?.()|[\]{}]/g, "\\$&")}`
    );

    if (prePreviewString !== "" && description.match(regex)) {
      setDescription(description.replace(prePreviewString, previewString));
    }
  }, [preview, expert, errorMessage]);

  /* Clear error layout for expression input if any of fields was changed */
  useEffect(() => {
    setErrorConfirm(false);
  }, [expression, expert, caseSensitive, colour]);

  const errorWidget = () => {
    if (!errorMessage) return null;
    // Don't display error for empty expression while editing (This error should be displayed only on "Confirm" button click)
    if (!errorConfirm && errorMessage === ERROR_MESSAGES.EMPTY_EXPRESSION) {
      return null;
    }

    return (
      <span className="field_inline_message" data-testid="error-widget">
        <span className="field_inline_icon">
          <Tooltip
            content={errorMessage.DETAILED}
            placement={tooltipPlacement.bottomRight}
          />
        </span>
        {/* Display inline message only on "Confirm" button click */}
        {errorConfirm && (
          <span className="brief_message">{errorMessage.BRIEF}</span>
        )}
      </span>
    );
  };

  return (
    <SlidingPanel
      panelRef={popupRef}
      dataTestId="wildcard-popup"
      onDismiss={closePopup}
    >
      <Modal.Header closeButton>
        <h6 className="edit_dictionary_page__wildcard_popup__title">
          <b>Edit {name && `"${name}" `}wildcard</b>
        </h6>
      </Modal.Header>
      <div className="edit_dictionary_page__wildcard_popup__content_wrapper">
        <Modal.Body>
          <p className="edit_dictionary_page__wildcard_popup__explanation">
            Wildcards are special characters that can stand in for any
            characters in a text value. Use them to search for text based on a
            specific pattern.
          </p>
          <p className="edit_dictionary_page__wildcard_popup__explanation">
            For example, &quot;52.*&quot; will find &quot;52.123&quot;,
            &quot;52.abc&quot; and &quot;52.a&quot;. While &quot;52.#&quot; will
            find just &quot;52.2&quot; and &quot;52.5&quot;.
          </p>
          <p className="edit_dictionary_page__wildcard_popup__explanation">
            If you prefer to use regular expressions, turn Regex mode on.
          </p>
        </Modal.Body>
        <div className="edit_dictionary_page__wildcard_popup__separator" />
        <Modal.Body>
          <div className="edit_dictionary_page__wildcard_popup__options">
            <span className="edit_dictionary_page__wildcard_popup__options__control_slot expand">
              <label htmlFor="mode-switch" className="control_label">
                Regex mode
              </label>
              <WildcardSwitch
                id="mode-switch"
                switchRef={wrapperRef}
                handleChange={handleSwitchChange}
                currentSwitchState={expert}
              />
            </span>
            <span className="edit_dictionary_page__wildcard_popup__options__control_slot inner_right_spacing middle">
              <label htmlFor="colour-button" className="control_label">
                Highlight Color
              </label>
              <ColourPicker
                selectedColour={colour}
                setSelectedColour={handleColourChange}
              />
            </span>
            <span className="edit_dictionary_page__wildcard_popup__options__control_slot last-vertical-positioning">
              <label
                htmlFor="case-sensitive-checkbox"
                className="control_label"
              >
                Case Sensitive
              </label>
              <Checkbox
                state={
                  caseSensitive ? checkedTypes.CHECKED : checkedTypes.EMPTY
                }
                handleChange={handleCaseSensitiveChange}
                disabled={false}
                id="case-sensitive-checkbox"
              />
            </span>
          </div>
          <label
            htmlFor="wildcard-popup-name"
            className={clsx(
              "edit_dictionary_page__wildcard_popup__name_label edit_dictionary_page__wildcard_popup__field_wrapper",
              errorConfirm && "error_confirm"
            )}
          >
            Term
            <div className="edit_dictionary_page__wildcard_popup__term field">
              <input
                ref={exprRef}
                type="text"
                id="wildcard-popup-name"
                value={expression}
                placeholder={
                  expert ? "Enter regular expression" : "Enter wildcard"
                }
                onChange={handleExpressionChange}
                className="field_input"
              />
              {errorWidget()}
            </div>
          </label>
          <div
            className={clsx("edit_dictionary_page__wildcard_popup__preview", {
              hide: expert,
            })}
          >
            Preview
            <span className="edit_dictionary_page__wildcard_popup__preview__field">
              {expression === "" ||
              !isExpressionValid(expert, expression) ||
              errorMessage === ERROR_MESSAGES.WILDCARD_NOT_VALID
                ? "Not available"
                : renderPreview(preview, colour)}
            </span>
          </div>
          <label
            htmlFor="wildcard-popup-description"
            className="edit_dictionary_page__wildcard_popup__description"
          >
            Description
            <textarea
              id="wildcard-popup-description"
              rows={2}
              value={description}
              onChange={handleDescriptionChange}
            />
          </label>
        </Modal.Body>
        <div className="edit_dictionary_page__wildcard_popup__separator" />
        {expert
          ? getRegularExpressionGuidanceText()
          : getWildcardExpressionGuidanceText()}
      </div>
      <Modal.Footer>
        <Button
          variant={buttonVariants.primary}
          onClick={handleConfirmClick}
          content="Confirm"
          dataTest="confirm-button"
        />
        <Button
          variant={buttonVariants.secondary}
          onClick={closePopup}
          content="Cancel"
          dataTest="cancel-button"
        />
      </Modal.Footer>
    </SlidingPanel>
  );
};

WildcardPopup.propTypes = {
  /**
   * Term data.
   */
  term: PropTypes.shape(termProps).isRequired,
};

export default WildcardPopup;
